import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine
} from 'recharts';
import {
  TrendingUp, TrendingDown, Minus, Award, AlertTriangle, Bell, RefreshCw, BarChart3, Calendar
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatPeriod = (p) => {
  if (!p) return '-';
  try {
    const [y, m] = p.split('-').map(Number);
    return new Date(y, m - 1, 1).toLocaleDateString('en-MY', { month: 'short', year: '2-digit' });
  } catch { return p; }
};

const statusColour = (pct) => {
  if (pct >= 90) return '#10B981';
  if (pct >= 75) return '#3B82F6';
  if (pct >= 50) return '#F59E0B';
  return '#E63946';
};

// Custom dot renderer — declared at module scope so React doesn't recreate it
// on each render (and so the linter is happy).
const InsightsDot = (props) => {
  const { cx, cy, payload, index } = props;
  if (cx == null || cy == null) return null;
  const c = statusColour(payload?.pct ?? 0);
  return <circle key={index} cx={cx} cy={cy} r={5} fill={c} stroke="#0B0B0F" strokeWidth={2} />;
};

export default function PerformanceInsightsView({ months = 6 }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedMonths, setSelectedMonths] = useState(months);

  // Keep selectedMonths in sync if parent ever changes the months prop
  useEffect(() => { setSelectedMonths(months); }, [months]);

  const load = async (n = selectedMonths) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const r = await axios.get(`${API}/scorecard/insights/me?months=${n}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(r.data);
    } catch (e) {
      console.error('insights load failed', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(selectedMonths); }, [selectedMonths]);

  if (loading && !data) {
    return (
      <div className="bg-[hsl(var(--surface))] border border-[hsl(var(--border))] rounded-xl p-8 text-center">
        <RefreshCw size={20} className="animate-spin mx-auto text-[#6B7280] mb-2" />
        <p className="text-sm text-[#9CA3AF]">Loading insights...</p>
      </div>
    );
  }

  if (!data?.success) {
    return (
      <div className="bg-[#F59E0B]/5 border border-[#F59E0B]/20 rounded-xl p-6" data-testid="insights-no-data">
        <p className="text-sm text-[#9CA3AF]">Tiada data scorecard untuk insights. Pastikan awak dah di-link dalam User Mappings.</p>
      </div>
    );
  }

  const series = (data.series || []).map((p) => ({
    ...p,
    label: formatPeriod(p.period),
    pct: Math.min(Math.round(p.overall_pct || 0), 100),
  }));

  const dir = data.trend_direction || 'flat';
  const DirIcon = dir === 'improving' ? TrendingUp : dir === 'declining' ? TrendingDown : Minus;
  const dirColour = dir === 'improving' ? '#10B981' : dir === 'declining' ? '#E63946' : '#6B7280';

  return (
    <div className="space-y-5" data-testid="performance-insights-view">
      {/* Top action: range selector */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <BarChart3 size={18} className="text-[#3B82F6]" />
          <h3 className="text-lg font-bold tracking-tight">Performance Insights</h3>
        </div>
        <div className="inline-flex p-1 rounded-lg bg-[hsl(var(--surface))] border border-[hsl(var(--border))]" data-testid="insights-range-toggle">
          {[3, 6, 12].map((n) => (
            <button
              key={n}
              onClick={() => setSelectedMonths(n)}
              className={`px-3 py-1.5 text-xs font-bold rounded-md transition-colors ${selectedMonths === n ? 'bg-[#3B82F6]/20 text-[#3B82F6]' : 'text-[hsl(var(--text-muted))] hover:text-white'}`}
              data-testid={`insights-range-${n}m`}
            >
              {n}M
            </button>
          ))}
        </div>
      </div>

      {/* Stat strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="bg-[hsl(var(--surface))] border border-[hsl(var(--border))] rounded-xl p-4">
          <div className="flex items-center gap-2 mb-2">
            <Calendar size={12} className="text-[#6B7280]" />
            <p className="text-[10px] uppercase tracking-wider text-[#6B7280] font-medium">{selectedMonths}-Month Average</p>
          </div>
          <p className="text-2xl font-bold" style={{ color: statusColour(data.average_pct) }} data-testid="insights-average">
            {Math.round(data.average_pct)}%
          </p>
        </div>

        <div className="bg-[hsl(var(--surface))] border border-[hsl(var(--border))] rounded-xl p-4">
          <div className="flex items-center gap-2 mb-2">
            <DirIcon size={12} style={{ color: dirColour }} />
            <p className="text-[10px] uppercase tracking-wider text-[#6B7280] font-medium">Trend</p>
          </div>
          <p className="text-sm font-bold uppercase tracking-wider" style={{ color: dirColour }} data-testid="insights-direction">
            {dir}
          </p>
          <p className="text-[10px] text-[hsl(var(--text-muted))] mt-0.5">
            Δ {data.trend_delta >= 0 ? '+' : ''}{data.trend_delta}%
          </p>
        </div>

        <div className="bg-[#10B981]/5 border border-[#10B981]/30 rounded-xl p-4">
          <div className="flex items-center gap-2 mb-2">
            <Award size={12} className="text-[#10B981]" />
            <p className="text-[10px] uppercase tracking-wider text-[#10B981] font-medium">Best Month</p>
          </div>
          {data.best ? (
            <>
              <p className="text-lg font-bold text-white" data-testid="insights-best">{formatPeriod(data.best.period)}</p>
              <p className="text-[11px] text-emerald-400">{Math.min(Math.round(data.best.overall_pct), 100)}% achievement</p>
            </>
          ) : <p className="text-xs text-[hsl(var(--text-muted))]">-</p>}
        </div>

        <div className="bg-[#E63946]/5 border border-[#E63946]/30 rounded-xl p-4">
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle size={12} className="text-[#E63946]" />
            <p className="text-[10px] uppercase tracking-wider text-[#E63946] font-medium">Worst Month</p>
          </div>
          {data.worst ? (
            <>
              <p className="text-lg font-bold text-white" data-testid="insights-worst">{formatPeriod(data.worst.period)}</p>
              <p className="text-[11px] text-red-400">{Math.min(Math.round(data.worst.overall_pct), 100)}% achievement</p>
            </>
          ) : <p className="text-xs text-[hsl(var(--text-muted))]">-</p>}
        </div>
      </div>

      {/* Line chart */}
      <div className="bg-[hsl(var(--surface))] border border-[hsl(var(--border))] rounded-xl p-4">
        <div className="flex items-center justify-between mb-3">
          <p className="text-xs uppercase tracking-wider text-[#9CA3AF] font-medium">Achievement % Over Time</p>
          <div className="flex items-center gap-3 text-[10px] text-[hsl(var(--text-muted))]">
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-[#10B981]" /> Healthy (≥75%)</span>
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-[#F59E0B]" /> Watch (50–74%)</span>
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-[#E63946]" /> Critical (&lt;50%)</span>
          </div>
        </div>
        <div className="h-64" data-testid="insights-line-chart">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={series} margin={{ top: 10, right: 10, left: -15, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#1F2937" />
              <XAxis dataKey="label" stroke="#6B7280" fontSize={11} />
              <YAxis stroke="#6B7280" fontSize={11} domain={[0, 100]} ticks={[0, 25, 50, 75, 100]} />
              <ReferenceLine y={75} stroke="#10B981" strokeDasharray="4 4" strokeOpacity={0.4} />
              <ReferenceLine y={50} stroke="#F59E0B" strokeDasharray="4 4" strokeOpacity={0.4} />
              <Tooltip
                contentStyle={{ backgroundColor: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: 8 }}
                labelStyle={{ color: '#fff', fontWeight: 600 }}
                formatter={(v, n, ctx) => {
                  if (n === 'pct') return [`${v}%`, 'Achievement'];
                  return [v, n];
                }}
              />
              <Line
                type="monotone"
                dataKey="pct"
                stroke="#3B82F6"
                strokeWidth={2.5}
                dot={InsightsDot}
                activeDot={{ r: 7 }}
                isAnimationActive
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Alert history */}
      <div className="bg-[hsl(var(--surface))] border border-[hsl(var(--border))] rounded-xl p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Bell size={14} className="text-[#F59E0B]" />
            <p className="text-xs uppercase tracking-wider text-[#9CA3AF] font-medium">Performance Alerts History</p>
          </div>
          <span className="text-[10px] text-[hsl(var(--text-muted))]" data-testid="insights-total-alerts">
            Total: {data.total_alerts} alerts in {selectedMonths} month{selectedMonths > 1 ? 's' : ''}
          </span>
        </div>
        {series.every(s => (s.alerts_count || 0) === 0) ? (
          <p className="text-xs text-emerald-400 py-3 text-center" data-testid="insights-no-alerts">
            🎉 Tiada performance alert dalam tempoh ini — keep it up!
          </p>
        ) : (
          <div className="space-y-2" data-testid="insights-alerts-list">
            {series.filter(s => (s.alerts_count || 0) > 0).map((row) => (
              <div key={row.period} className="flex items-center gap-3 p-2 rounded-lg bg-[hsl(var(--surface-elevated))] border border-[hsl(var(--border))]" data-testid={`insights-alert-row-${row.period}`}>
                <span className="text-[11px] font-semibold text-white min-w-[60px]">{row.label}</span>
                <div className="flex-1 flex flex-wrap items-center gap-1.5">
                  {(row.alerts || []).map((a, i) => (
                    <span key={i} className="text-[9px] px-1.5 py-0.5 rounded bg-[#F59E0B]/15 text-[#F59E0B] border border-[#F59E0B]/30 font-medium">
                      Day {a.checkpoint_day} • {Math.round(a.achievement_pct || 0)}% &lt; {Math.round(a.threshold || 0)}%
                    </span>
                  ))}
                </div>
                <span className="text-[10px] text-[hsl(var(--text-muted))]">{row.alerts_count} alert{row.alerts_count > 1 ? 's' : ''}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
